import { memo, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReviewButton from '../reviewSubmitButton/reviewButton';
import styles from './reviewPopup.module.css';
import Rating from 'react-rating';
import starColor from '../../icons/star-color.svg';
import starTransparent from '../../icons/star-transparent.svg';
import Popup from '../popup/popup';
import Button from '../button/button';
import success from '../../icons/success.svg';
import { initialRatings } from './reviewPopup.helper';
import { editReview, sendReview } from '../../services/api';
import { UserDataContext } from '../../Context/Context';

const ReviewForm = ({ titleText, closeType, ratings, setRatings, reviewText, setReviewText, sendReview, showMessage, setShowMessage, closePopup, error, setError, setRefreshReviews, disabled }) => {
  const ratingOptions = useMemo(() => ratings.sort((a, b) => a.RateCriterium?.name.localeCompare(b.RateCriterium?.name)).map((elem) => ({
    title: elem?.RateCriterium?.name,
    value: elem?.rates || 0,
    onChange: (val) => setRatings((prev) => ([...prev.filter(e => e?.RateCriterium?.name !== elem?.RateCriterium?.name), { ...elem, rates: val }])),
  })), [ratings, setRatings]);

  return (
    <div className={styles.popupContainer}>
      <Popup visible={showMessage} id={styles.message} hideLogo={true}>
        {!error ? <>
          <img src={success} alt="success" style={{ width: '40px', height: '40px' }} />
          <p className={styles.alertText}>Your review has been {closeType === 'reviewEdit' ? 'updated' : 'submitted'} successfully.</p>
        </> : <p className={styles.alertText}>Unable to submit your review. Please try again or reach out to support for assistance.</p>}
        <Button
          onClick={() => {
            setShowMessage(false);
            closePopup(closeType);
            setError(false);
          }}
          style={{ marginTop: '40px', maxWidth: '350px' }}
        > Ok</Button>
      </Popup>
      <div className={styles.popup_wrapper}>
        <button onClick={() => closePopup(closeType)} className={styles.close} aria-label="Close">
          &#x2715;
        </button>
        <div className={styles.body}>
          <h2>{titleText}</h2>
          <textarea
            placeholder="Write a review...."
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            style={{ resize: 'none' }}
          />
          <ul className={styles.review_wrapper}>
            {ratingOptions.map(({ title, value, onChange }) => (
              <li key={title}>
                <span>{title}:</span>
                <Rating
                  key={title}
                  initialRating={value}
                  onChange={onChange}
                  stop={10}
                  emptySymbol={<img src={starTransparent} className={styles.starIcon} alt="" />}
                  fullSymbol={<img src={starColor} className={styles.starIcon} alt="" />}
                  fractions={1}
                />
              </li>
            ))}
          </ul>
          <ReviewButton type="write" onClick={sendReview} disabled={disabled} />
        </div>
      </div>
    </div>
  );
};

const ReviewPopup = ({ reviewEditPopup, reviewWritePopup, closePopup, hotelReviewData, hotelID, googlePlaceId, setRefreshReviews, loading, setLoading }) => {
  const [ratings, setRatings] = useState(initialRatings.Rates);
  const [reviewText, setReviewText] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(false)
  const { user } = useContext(UserDataContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (hotelReviewData && hotelReviewData?.Rates) setRatings(hotelReviewData.Rates);
    setReviewText(hotelReviewData?.comment || '');
  }, [hotelReviewData]);

  useEffect(() => {
    document.body.style.overflow = reviewEditPopup || reviewWritePopup ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [reviewEditPopup, reviewWritePopup]);

  const sendHotelReview = useCallback(async () => { 
    if (!user.loggined) {
      navigate('/user?action=login');
      return;
    }

    setDisabled(true)
    const ratingsObj = ratings.reduce((acc, e) => {
      acc[e.RateCriterium.name.toLowerCase()] = e.rates;
      return acc;
    }, {});
    const targetHotelId = hotelReviewData?.hotelID || hotelID;
    const targetGooglePlaceId = hotelReviewData?.googlePlaceId || googlePlaceId;
    let submitReviewStatus = reviewWritePopup
      ? await sendReview(targetHotelId, { text: reviewText, ...ratingsObj }, targetGooglePlaceId)
      : await editReview(targetHotelId, { text: reviewText, ...ratingsObj }, targetGooglePlaceId);
    setShowMessage(true)
    if (submitReviewStatus.ok) {
      setRefreshReviews(prev => !prev);
      setError(false);
      if (typeof loading === 'object') setLoading(prev => ({ ...prev, update: true }))
    }
    else setError(true);
    setDisabled(false)
  }, [user.loggined, navigate, reviewText, ratings, hotelReviewData, reviewWritePopup, hotelID, googlePlaceId, setRefreshReviews, setLoading , loading]);
  
  return (
    <>
      {(reviewEditPopup || reviewWritePopup) && ( typeof loading === 'object' ? !loading.edit : !loading) && (
        <ReviewForm
          titleText={reviewEditPopup ? "Edit a Review" : "Write a Review"}
          closeType={reviewEditPopup ? "reviewEdit" : "reviewWrite"}
          ratings={ratings}
          setRatings={setRatings}
          reviewText={reviewText}
          setReviewText={setReviewText}
          sendReview={sendHotelReview}
          showMessage={showMessage}
          setShowMessage={setShowMessage}
          closePopup={closePopup}
          error={error}
          setError={setError}
          setRefreshReviews={setRefreshReviews}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default memo(ReviewPopup);
